import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  AdPlatformReportingClient,
  DateTimeRange,
  ReportingFlightReport,
  ReportingPerformanceMetrics,
  SponsoredListingsCampaign,
  SponsoredListingsFlight,
} from 'src/WebApiController';

/**
 * Applies reporting metrics to a flight object, returning a new clone with metrics set.
 */
const applyFlightMetrics = (
  flight: SponsoredListingsFlight,
  metrics: ReportingPerformanceMetrics
): SponsoredListingsFlight => {
  const flightWithMetrics: SponsoredListingsFlight = { ...flight };
  flightWithMetrics.historicPotentialImpressions = metrics.eventImpressions;
  flightWithMetrics.actualImpressions = metrics.adImpressions;
  flightWithMetrics.impressionShare =
    (metrics.eventImpressions ?? 0) > 0
      ? (metrics.adImpressions ?? 0) / (metrics.eventImpressions ?? 0)
      : null;
  flightWithMetrics.clicks = metrics.clicks;
  flightWithMetrics.salesCount = metrics.adSalesCount;
  flightWithMetrics.salesProceeds = metrics.adSalesProceeds;
  flightWithMetrics.flightSpend = metrics.adSpendUSD;
  flightWithMetrics.potentialSales = metrics.potentialSales;
  flightWithMetrics.potentialRevenue = metrics.potentialSalesProceeds;
  return flightWithMetrics;
};

/**
 * Custom hook to fetch and merge reporting metrics with campaigns data.
 * Fetches both campaign-level and flight-level metrics, and merges them
 * into campaigns and their nested flight objects.
 *
 * @param campaigns - Array of campaigns to enrich with reporting metrics
 * @param dateRangeFilter - Optional date range to filter metrics by. Falls back to all historical data (2020-01-01 to today) when not provided.
 * @returns Object containing the campaigns array enriched with reporting metrics
 *   (or original campaigns if metrics not available) and an `isLoading` flag that
 *   is true while either the campaign-metrics or flight-metrics query is in flight.
 */
export const useCampaignsTableMetricsData = (
  campaigns: SponsoredListingsCampaign[] | null | undefined,
  dateRangeFilter?: DateTimeRange | null
): {
  campaigns: SponsoredListingsCampaign[] | null | undefined;
  isLoading: boolean;
} => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();

  // Extract campaign IDs for metrics query
  const campaignIds = useMemo(
    () => (campaigns ?? []).map((c) => c.campaignId),
    [campaigns]
  );

  // Build campaign-flights request for flight metrics query
  const campaignFlights = useMemo(
    () =>
      (campaigns ?? []).map((c) => ({
        campaignId: c.campaignId,
        flightIds: [
          ...(c.activeFlight ? [c.activeFlight.flightId] : []),
          ...(c.flights?.map((f) => f.flightId) ?? []),
        ],
      })),
    [campaigns]
  );

  // Determine date range for metrics query.
  const metricsDateRange = useMemo(() => {
    return {
      startDate: dateRangeFilter?.start
        ? new Date(dateRangeFilter.start).toISOString()
        : new Date('2025-01-01').toISOString(),
      endDate: dateRangeFilter?.end
        ? new Date(dateRangeFilter.end).toISOString()
        : new Date().toISOString(),
    };
  }, [dateRangeFilter]);

  // Query to fetch campaign metrics and flight metrics
  const shouldQueryMetrics = useMemo(
    () =>
      activeAccountWebClientConfig.activeAccountId != null &&
      campaignIds.length > 0,
    [activeAccountWebClientConfig.activeAccountId, campaignIds.length]
  );

  // Query key for campaign metrics
  const campaignMetricsQueryKey = useMemo(
    () => [
      'AdPlatformReportingClient.getCampaignMetricsByIds',
      activeAccountWebClientConfig.activeAccountId,
      campaignIds,
      metricsDateRange.startDate,
      metricsDateRange.endDate,
    ],
    [
      activeAccountWebClientConfig.activeAccountId,
      campaignIds,
      metricsDateRange.startDate,
      metricsDateRange.endDate,
    ]
  );

  // Query to fetch campaign metrics
  const campaignMetricsQuery = useQuery({
    queryKey: campaignMetricsQueryKey,
    queryFn: async () => {
      if (!shouldQueryMetrics) {
        return null;
      }

      return tryInvokeApi(
        async () => {
          const client = new AdPlatformReportingClient(
            activeAccountWebClientConfig
          );
          const result = await client.getCampaignMetricsByIds({
            campaignIds: campaignIds,
            startDate: metricsDateRange.startDate,
            endDate: metricsDateRange.endDate,
          });
          return result;
        },
        (error) => {
          trackError(
            'AdPlatformReportingClient.getCampaignMetricsByIds',
            error,
            {
              campaignIds,
              startDate: metricsDateRange.startDate,
              endDate: metricsDateRange.endDate,
            }
          );
        }
      );
    },
    enabled: shouldQueryMetrics,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });

  // Query key for flight metrics
  const flightMetricsQueryKey = useMemo(
    () => [
      'AdPlatformReportingClient.getFlightsMetricsByCampaignIds',
      activeAccountWebClientConfig.activeAccountId,
      campaignFlights,
      metricsDateRange.startDate,
      metricsDateRange.endDate,
    ],
    [
      activeAccountWebClientConfig.activeAccountId,
      campaignFlights,
      metricsDateRange.startDate,
      metricsDateRange.endDate,
    ]
  );

  // Query to fetch flight metrics
  const flightMetricsQuery = useQuery({
    queryKey: flightMetricsQueryKey,
    queryFn: async () => {
      if (!shouldQueryMetrics) {
        return null;
      }

      return tryInvokeApi(
        async () => {
          const client = new AdPlatformReportingClient(
            activeAccountWebClientConfig
          );
          const result = await client.getFlightsMetricsByCampaignIds({
            campaignFlights: campaignFlights,
            startDate: metricsDateRange.startDate,
            endDate: metricsDateRange.endDate,
          });
          return result;
        },
        (error) => {
          trackError(
            'AdPlatformReportingClient.getFlightsMetricsByCampaignIds',
            error,
            {
              campaignFlights,
              startDate: metricsDateRange.startDate,
              endDate: metricsDateRange.endDate,
            }
          );
        }
      );
    },
    enabled: shouldQueryMetrics,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });

  // Merge campaign and flight metrics into campaigns data
  const campaignsWithMetrics = useMemo(() => {
    if (
      !campaigns ||
      (!campaignMetricsQuery.data?.campaigns &&
        !flightMetricsQuery.data?.flights)
    ) {
      return campaigns;
    }

    // Build campaign metrics map
    const campaignMetricsMap = new Map(
      (campaignMetricsQuery.data?.campaigns ?? []).map((report) => [
        report.campaignId,
        report,
      ])
    );

    // Build flight metrics map (flightId -> report)
    const flightMetricsMap = new Map<number, ReportingFlightReport>(
      (flightMetricsQuery.data?.flights ?? []).map((report) => [
        report.flightId!,
        report,
      ])
    );

    return campaigns.map((campaign) => {
      const campaignReport = campaignMetricsMap.get(campaign.campaignId);

      // Clone the campaign
      const campaignWithMetrics: SponsoredListingsCampaign = { ...campaign };

      // Apply campaign-level metrics
      if (campaignReport?.totalMetrics) {
        campaignWithMetrics.historicPotentialImpressions =
          campaignReport.totalMetrics.eventImpressions;
        campaignWithMetrics.actualImpressions =
          campaignReport.totalMetrics.adImpressions;
        campaignWithMetrics.impressionShare =
          (campaignReport.totalMetrics.eventImpressions ?? 0) > 0
            ? (campaignReport.totalMetrics.adImpressions ?? 0) /
              (campaignReport.totalMetrics.eventImpressions ?? 0)
            : null;
        campaignWithMetrics.clicks = campaignReport.totalMetrics.clicks;
        campaignWithMetrics.salesCount =
          campaignReport.totalMetrics.adSalesCount;
        campaignWithMetrics.salesProceeds =
          campaignReport.totalMetrics.adSalesProceeds;
        campaignWithMetrics.totalSpend = campaignReport.totalMetrics.adSpendUSD;
        campaignWithMetrics.potentialSales =
          campaignReport.totalMetrics.potentialSales;
        campaignWithMetrics.potentialRevenue =
          campaignReport.totalMetrics.potentialSalesProceeds;
      }

      // Apply flight-level metrics to activeFlight
      if (campaignWithMetrics.activeFlight) {
        const flightReport = flightMetricsMap.get(
          campaignWithMetrics.activeFlight.flightId
        );
        if (flightReport?.metrics) {
          campaignWithMetrics.activeFlight = applyFlightMetrics(
            campaignWithMetrics.activeFlight,
            flightReport.metrics
          );
        }
      }

      // Apply flight-level metrics to flights array
      if (campaignWithMetrics.flights) {
        campaignWithMetrics.flights = campaignWithMetrics.flights.map(
          (flight) => {
            const flightReport = flightMetricsMap.get(flight.flightId);
            if (flightReport?.metrics) {
              return applyFlightMetrics(flight, flightReport.metrics);
            }
            return flight;
          }
        );
      }

      return campaignWithMetrics;
    });
  }, [campaigns, campaignMetricsQuery.data, flightMetricsQuery.data]);

  return {
    campaigns: campaignsWithMetrics,
    isLoading: campaignMetricsQuery.isLoading || flightMetricsQuery.isLoading,
  };
};
